import React, {
  useState,
  CSSProperties,
  useEffect,
  useRef,
  useContext,
} from 'react'
import { useEffectOnce } from 'usehooks-ts'
import styled from 'styled-components'
import ReactECharts from 'components/ReactECharts'
import { color, type EChartsOption, type SetOptionOpts } from 'echarts'
import * as d3 from 'd3'
import { nest } from 'd3-collection'
import './tooltip.scss'
import GlobalContext from 'Contexts/GlobalContext'
import { de } from 'Globals/locale'

export interface ReactEChartsProps {
  option: EChartsOption
  style?: CSSProperties
  settings?: SetOptionOpts
  loading?: boolean
  theme?: string
}
type ChartRefType = HTMLDivElement & {
  closeTooltip?: () => void
}

const ColumnChart = ({
  tenant,
  colorMode,
  data,
  index,
  colors,
  iso,
  activeTooltip,
  setActiveTooltip,
  isDetailView,
  isRightSide,
}: {
  tenant?: object
  colorMode?: string
  data: any
  index: number
  colors: any
  iso: string
  activeTooltip: number
  setActiveTooltip: any
  isDetailView?: boolean
  isRightSide?: boolean
}) => {
  const [isMobile, setIsMobile] = useState(false)
  const [isLargeMobile, setIsLargeMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)
  const [isWide, setIsWide] = useState(false)
  const [chartOptions, setChartOptions] = useState({})
  const [allYears, setAllYears] = useState(Array<string>)
  const { locale } = useContext(GlobalContext) ?? { locale: de }

  const chartRef = useRef<ChartRefType>(null)

  const handleResize = (): void => {
    setIsWide(window.innerWidth > 1023)
    setIsTablet(window.innerWidth >= 769 && window.innerWidth < 1024)
    setIsLargeMobile(window.innerWidth > 599 && window.innerWidth < 769)
    setIsMobile(window.innerWidth < 600)
  }

  useEffectOnce(() => {
    // set height when window is resized
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  })

  const handleChartClick = () => {
    setActiveTooltip(index)
  }
  useEffect(() => {
    if (activeTooltip !== index) {
      chartRef?.current?.closeTooltip?.()
    }
  }, [activeTooltip, index])

  const commatize = (word: any) => {
    const replaced = word.toString().replace('.', ',')
    return replaced.indexOf(',0') > 1 && replaced.slice(-2) === ','
      ? replaced.replace(',0', '')
      : replaced
  }

  useEffect(() => {
    // console.log('columnchart:', data)
    setAllYears(
      data.map((item: any) => {
        return item.key
      })
    )
    // console.log('all years:', allYears)
  }, [])

  // echarts options
  useEffect(() => {
    // prepare data for display
    const xAxisLabels = data.map((item: any) => {
      return item.key.slice(0, 4)
    })

    //put all objects in data values array into one array
    const values = data.map((item: any) => {
      return item.values
    })
    // flatten all arrays in values into one array
    const flatValues = values.flat().sort(function (a: any, b: any) {
      return d3.descending(a['Anteil_Sitze'], b['Anteil_Sitze'])
    })
    // console.log('flatValues', flatValues)

    // nest data by party
    const partyData = nest()
      .key(function (d: any) {
        return d['Partei_kurz_DE']
      })
      .entries(flatValues)
    // console.log('ISO', iso)
    // console.log('partyData', partyData)

    // hard coded exceptions for classification
    let classificationIndex = -1
    switch (iso) {
      case 'pol':
        classificationIndex = 0
        break
      case 'isl':
        classificationIndex = 0
        break
      case 'prt':
        classificationIndex = 0
        break
      default:
        classificationIndex = -1
    }

    // filter partyData by Linksaussen into new array called farleftData
    const farleftData = partyData.filter((item: { values: any }) => {
      const classIndex =
        classificationIndex >= 0 ? classificationIndex : item.values.length - 1
      return (
        item.values[classIndex].Linksaussen &&
        !item.values[classIndex].Populistisch
      )
    })
    // console.log('farleftData', farleftData)

    const leftPopulistData = partyData.filter((item: { values: any }) => {
      const classIndex =
        classificationIndex >= 0 ? classificationIndex : item.values.length - 1
      return (
        item.values[classIndex].Populistisch &&
        item.values[classIndex].Linksaussen
      )
    })
    // console.log('leftPopulistData', leftPopulistData)

    const othersPopulistData = partyData.filter((item: { values: any }) => {
      const classIndex =
        classificationIndex >= 0 ? classificationIndex : item.values.length - 1
      return (
        item.values[classIndex].Populistisch &&
        !item.values[classIndex].Linksaussen &&
        !item.values[classIndex].Rechtsaussen
      )
    })
    // console.log('othersPopulistData', othersPopulistData)

    const rightPopulistData = partyData.filter((item: { values: any }) => {
      const classIndex =
        classificationIndex >= 0 ? classificationIndex : item.values.length - 1
      return (
        item.values[classIndex].Populistisch &&
        item.values[classIndex].Rechtsaussen
      )
    })
    // console.log('rightPopulistData', rightPopulistData)

    const farrightData = partyData.filter((item: { values: any }) => {
      const classIndex =
        classificationIndex >= 0 ? classificationIndex : item.values.length - 1
      return (
        item.values[classIndex].Rechtsaussen &&
        !item.values[classIndex].Populistisch
      )
    })
    // console.log('farrightData', farrightData)

    const othersData = partyData.filter((item: { values: any }) => {
      const classIndex =
        classificationIndex >= 0 ? classificationIndex : item.values.length - 1
      return (
        !item.values[classIndex].Linksaussen &&
        !item.values[classIndex].Populistisch &&
        !item.values[classIndex].Rechtsaussen
      )
    })
    // console.log('othersData', othersData)

    const stackedData = farrightData.concat(
      rightPopulistData,
      othersPopulistData,
      leftPopulistData,
      farleftData,
      othersData
    )

    // custom hard coded sorting for Polen
    if (iso === 'pol') {
      // move party PiS in 2001-09-23 to the end
      const partyIndex = stackedData.findIndex(
        (item: any) => item.key === 'PiS'
      )
      const party = stackedData[partyIndex]
      const valuesToMove = party.values.find(
        (item: any) => item.Jahr === '2001-09-23'
      )
      const partyValues = party.values.filter(
        (item: any) => item.Jahr !== '2001-09-23'
      )
      stackedData[partyIndex].values = partyValues
      //add again at the end of the array
      stackedData.push({
        key: 'PiS',
        values: [valuesToMove],
      })
    }

    // custom hard coded sorting for Island
    if (iso === 'isl') {
      // move party F in2017-10-28 to the end
      const partyIndex = stackedData.findIndex((item: any) => item.key === 'F')
      const party = stackedData[partyIndex]
      let valuesToMove = party.values.find(
        (item: any) => item.Jahr === '2017-10-28'
      )
      let values2ToMove = party.values.find(
        (item: any) => item.Jahr === '2021-09-25'
      )
      const partyValues = party.values.filter(
        (item: any) => item.Jahr !== '2017-10-28' && item.Jahr !== '2021-09-25'
      )
      stackedData[partyIndex].values = partyValues
      //add again at the end of the array
      stackedData.push({
        key: 'F',
        values: [valuesToMove, values2ToMove],
      })
    }

    // custom hard coded sorting for Litauen
    if (iso === 'ltu') {
      // move party DP in 2004-10-24 to the beginning
      const partyIndex = stackedData.findIndex((item: any) => item.key === 'DP')
      const party = stackedData[partyIndex]
      const valuesToMove = party.values.find(
        (item: any) => item.Jahr === '2004-10-24'
      )
      const partyValues = party.values.filter(
        (item: any) => item.Jahr !== '2004-10-24'
      )
      stackedData[partyIndex].values = partyValues
      //add again at the beginning of the array
      stackedData.unshift({
        key: 'DP',
        values: [valuesToMove],
      })
    }

    // custom hard coded sorting for Portugal
    if (iso === 'prt') {
      // move party BE in 2024-03-10 to the beginning
      let partyIndex = stackedData.findIndex((item: any) => item.key === 'BE')
      let party = stackedData[partyIndex]
      let valuesToMove = party.values.find(
        (item: any) => item.Jahr === '2024-03-10'
      )
      let partyValues = party.values.filter(
        (item: any) => item.Jahr !== '2024-03-10'
      )
      stackedData[partyIndex].values = partyValues
      //add again at the beginning of the array
      stackedData.unshift({
        key: 'BE',
        values: [valuesToMove],
      })

      // move party CDU in 2024-03-10 to the beginning
      partyIndex = stackedData.findIndex((item: any) => item.key === 'CDU')
      party = stackedData[partyIndex]
      valuesToMove = party.values.find(
        (item: any) => item.Jahr === '2024-03-10'
      )
      partyValues = party.values.filter(
        (item: any) => item.Jahr !== '2024-03-10'
      )
      stackedData[partyIndex].values = partyValues
      //add again at the beginning of the array
      stackedData.unshift({
        key: 'CDU',
        values: [valuesToMove],
      })

      // move party BE in 2024-03-10 to the beginning
      partyIndex = stackedData.findIndex((item: any) => item.key === 'CH')
      party = stackedData[partyIndex]
      valuesToMove = party.values.find(
        (item: any) => item.Jahr === '2024-03-10'
      )
      partyValues = party.values.filter(
        (item: any) => item.Jahr !== '2024-03-10'
      )
      stackedData[partyIndex].values = partyValues
      //add again at the beginning of the array
      stackedData.unshift({
        key: 'CH',
        values: [valuesToMove],
      })
    }

    const chartSeriesData = stackedData.map((party: any, index: number) => {
      // console.log('party', party)
      const lastIndex = party.values.length - 1
      const classification =
        party.values[lastIndex].Populistisch &&
        party.values[lastIndex].Rechtsaussen
          ? 'right'
          : party.values[lastIndex].Populistisch &&
            party.values[lastIndex].Linksaussen
          ? 'left'
          : party.values[lastIndex].Rechtsaussen
          ? 'farright'
          : party.values[lastIndex].Linksaussen
          ? 'farleft'
          : party.values[lastIndex].Populistisch
          ? 'middlepopulist'
          : 'middle'

      const seriesData = allYears?.map((year: any) => {
        // console.log('year', year)
        const electionData = party.values.find((obj: any) => obj.Jahr === year)
        // console.log('electionData:', electionData)
        const classification =
          electionData?.Populistisch && electionData?.Rechtsaussen
            ? 'right'
            : electionData?.Populistisch && electionData?.Linksaussen
            ? 'left'
            : electionData?.Rechtsaussen
            ? 'farright'
            : electionData?.Linksaussen
            ? 'farleft'
            : electionData?.Populistisch
            ? 'middlepopulist'
            : 'middle'
        const value = electionData
          ? (electionData.Anteil_Sitze * 100).toFixed(1)
          : 0
        return {
          value: value,
          itemStyle: { color: colors[classification] },
        }
      })

      return {
        name: party.key + '=>' + party.values[0].Partei_DE, // party.key, // party.values[0].Partei_DE,
        type: 'bar',
        barCategoryGap: isDetailView ? '10%' : '5%',
        stack: 'total',
        label: {
          show: isDetailView ? true : false, // classification === 'middle' ? false : true,
          fontSize: 12,
          formatter: function (params: any) {
            // console.log('params:', params)
            const partylabels = params.seriesName.split('=>')
            return +params.value >= 10 && classification !== 'middle'
              ? partylabels[0]
              : '' // +params.value > 20 ? params.value + '%' : ''
          },
          color: '#fff',
        },
        emphasis: {
          focus: 'series',
        },
        // color: colors[classification],
        data: seriesData,
        silent: true,
      }
    })
    // console.log('chartSeriesData:', chartSeriesData)

    const legendData = partyData.map((party: any, index: number) => {
      // console.log('party', party)
      const ausr = parseFloat(party.values[0]['Ausrichtung'])
      return ausr > 7 || ausr < 3
        ? {
            name: party.key,
          }
        : ''
    })

    const flagImage = require('../../images/' + iso + '.svg')
    // echart options
    const chartOptions: ReactEChartsProps['option'] = {
      height: isDetailView
        ? isMobile
          ? '230px'
          : '250px'
        : isMobile
        ? '130px'
        : '150px',
      textStyle: {
        fontFamily:
          '"IBM Plex Sans","Helvetica",Arial,Verdana,Helvetica,sans-serif',
      },
      tooltip: {
        show: true,
        // xtriggerOn: 'mousemove|click',
        trigger: 'axis',
        hideDelay: 0,
        // enterable: true,
        backgroundColor: colorMode === 'dark' ? '#343434' : '#fff',
        axisPointer: {
          axis: 'x',
          type: 'shadow', // 'shadow' as default; can also be 'line' or 'shadow'
        },
        textStyle: {
          fontSize: isMobile ? 13 : 13,
        },
        formatter: function (params: any): string {
          // console.log('tooltip params', params)
          let tooltipContent = ''
          if (params) {
            tooltipContent +=
              '<div style="border-bottom: 1px solid #ccc; padding-bottom: 0.7rem; text-align: left; margin-bottom: 1rem; width: 100%;"><div style="width: 1.4rem; display: inline-block; margin-right: 0.8rem;"><img src=' +
              flagImage +
              ' alt=' +
              iso +
              ' style="border: 1px solid #eee; filter: saturate(85%);"/></div>'
            const closer =
              '<div style="font-size: 1.8rem; position: absolute; top: 9px; right: 14px;">×</div>'
            tooltipContent += isMobile || isLargeMobile ? closer : ''
            const year = params[0].axisValue
            const flatStacks = stackedData.flatMap((item: any) => item.values)
            const targetJahr = flatStacks.find((yr: any) =>
              yr.Jahr.includes(year)
            )
            const totalSitze = targetJahr ? targetJahr?.Sitze_total : undefined
            tooltipContent +=
              '<b class="year" style="width: unset;">' +
              year +
              '</b>' +
              ' (' +
              totalSitze +
              ' ' +
              locale.seats +
              ')</div>'
            const parties = params.filter((item: any) => {
              return item.value !== '0'
            }, [])
            // console.log('parties:', parties)

            // sort parties by the following color order then by value
            const colorOrder = [
              colors['farright'],
              colors['right'],
              colors['middlepopulist'],
              colors['left'],
              colors['farleft'],
              colors['middle'],
            ]
            parties.sort(function (a: any, b: any) {
              const aColor = a.color
              const bColor = b.color
              const aValue = a.value
              const bValue = b.value
              if (aColor === bColor) {
                return bValue - aValue
              }
              return colorOrder.indexOf(aColor) > colorOrder.indexOf(bColor)
                ? 1
                : -1
            })

            parties.forEach((item: any, i: number) => {
              // console.log('item:', item)
              // console.log('item.seriesName:', item.seriesName)
              const partylabels = item.seriesName.split('=>')
              const targetObject = stackedData.filter(
                (obj: any) => obj.key === partylabels[0]
              )

              let partySeats = 0
              let targetYr = undefined
              if (targetObject.length === 1) {
                targetYr = targetObject[0].values.find((yr: any) => {
                  return yr.Jahr.includes(year)
                })
              } else {
                // custom hardcoded exceptions
                if (
                  iso === 'ltu' &&
                  partylabels[0] === 'DP' &&
                  year === '2004'
                ) {
                  targetYr = targetObject[0].values.find((yr: any) => {
                    return yr.Jahr.includes(year)
                  })
                } else {
                  // take the second found object, since we added it to the end of the array
                  targetYr = targetObject[1].values.find((yr: any) => {
                    return yr.Jahr.includes(year)
                  })
                }
              }
              partySeats = targetYr ? targetYr?.Sitze : undefined

              if (item.value > 0) {
                tooltipContent += `<div class='party' style="width: ${
                  isDetailView ? '280px' : isMobile ? '160px' : '300px'
                }; line-height: 1.8rem; margin-bottom: 0.4rem; display: grid; grid-template-columns: 1fr 25fr;"><div class="square" style="background-color: ${
                  item.color
                }; vertical-align: top; top: 4px;">&nbsp;</div>`
                tooltipContent += `<div style="white-space: normal; display: inline;">${
                  isDetailView || !isMobile ? partylabels[1] : partylabels[0]
                }: <span style="white-space: nowrap;"><b>${commatize(
                  item.value
                )}%</b>&nbsp;(${partySeats} ${
                  partySeats > 1 ? locale.seats : locale.seat
                })</span></div></div>`
              }
            })
          }
          return '<div class="tooltip">' + tooltipContent + '</div>'
        },
        valueFormatter: function (value) {
          return value ? commatize(value) + '%' : 'missing'
        },
        position: function (point, params, dom, rect, size) {
          /*
          console.log('point', point)
          console.log('params', params)
          console.log('dom', dom)
          console.log('rect', rect)
          console.log('size', size)
          */
          return {
            left: isDetailView
              ? isRightSide
                ? isMobile
                  ? '5%'
                  : '-90%'
                : isMobile
                ? '5%'
                : '105%'
              : isWide
              ? (index / 5) % 1 < 0.41
                ? '105%'
                : -(size.contentSize[0] + 5)
              : isTablet
              ? (index / 4) % 1 < 0.26
                ? '105%'
                : -(size.contentSize[0] + 5)
              : isLargeMobile
              ? (index / 3) % 1 < 0.66
                ? '105%'
                : -(size.contentSize[0] + 5)
              : isMobile
              ? index % 2
                ? -size.contentSize[0]
                : '102%'
              : -(size.contentSize[0] + 5),
            top: isDetailView ? (isMobile ? '82%' : '2%') : isMobile ? -30 : 5,
          }
        },
      },
      animation: false,
      legend: {
        show: false,
        bottom: isMobile ? '9%' : 0, // legend underneeth chart
        width: '90%',
        icon: 'rect',
        itemWidth: 15,
        textStyle: {
          fontSize: 10,
        },
        selectedMode: false,
        data: legendData,
      },
      grid: {
        top: '3%',
        left: '1%',
        right: '0',
        bottom: '0',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: xAxisLabels,
        axisLabel: {
          inside: false,
          margin: 5,
          fontSize: isDetailView ? 12 : 9,
        },
        axisTick: {
          show: false,
        },
        axisLine: {
          show: false,
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        axisLabel: {
          show: isDetailView,
          formatter: '{value}%', // Add a percentage sign to the labels
          fontSize: isDetailView ? 12 : 9,
        },
        axisTick: {
          show: isDetailView,
        },
        axisLine: {
          show: false,
        },
        splitLine: {
          show: isDetailView,
        },
      },
      series: chartSeriesData,
      /* Dummy Data
      [ 
        {
          name: 'Links aussen',
          type: 'bar',
          barCategoryGap: '5%',
          stack: 'total',
          label: {
            show: true,
            fontSize: 10,
            formatter: function (params) {
              return +params.value > 10 ? commatize(params.value) + '%' : ''
            },
          },
          emphasis: {
            focus: 'series',
          },
          color: '#691A1F',
          data: [5, 5, 5, 5, 5],
          silent: true,
        },
        {
          name: 'SP',
          type: 'bar',
          stack: 'total',
          label: {
            show: true,
            fontSize: 10,
            formatter: function (params) {
              return +params.value > 10 ? params.value + '%' : ''
            },
          },
          emphasis: {
            focus: 'series',
          },
          color: '#D3343F',
          data: [20, 20, 20, 20, 20],
          silent: true,
        },
        {
          name: 'FDP',
          type: 'bar',
          stack: 'total',
          label: {
            show: true,
            fontSize: 10,
            formatter: function (params) {
              return +params.value > 10 ? params.value + '%' : ''
            },
          },
          emphasis: {
            focus: 'series',
          },
          color: '#2CA29F',
          data: [25, 25, 25, 25, 25],
          silent: true,
        },
        {
          name: 'CVP',
          type: 'bar',
          stack: 'total',
          label: {
            show: true,
            fontSize: 10,
            formatter: function (params) {
              return +params.value > 10 ? params.value + '%' : ''
            },
          },
          emphasis: {
            focus: 'series',
          },
          color: '#2CA29F',
          data: [25, 25, 25, 25, 25],
          silent: true,
        },
        {
          name: 'SVP',
          type: 'bar',
          stack: 'total',
          label: {
            show: true,
            fontSize: 10,
            formatter: function (params) {
              return +params.value > 10 ? params.value + '%' : ''
            },
          },
          emphasis: {
            focus: 'series',
          },
          color: '#E16D00',
          data: [20, 20, 20, 20, 20],
          silent: true,
        },
        {
          name: 'Rechts aussen',
          type: 'bar',
          stack: 'total',
          label: {
            show: true,
            fontSize: 10,
            formatter: function (params) {
              return +params.value > 10 ? params.value + '%' : ''
            },
          },
          emphasis: {
            focus: 'series',
          },
          color: '#964800',
          data: [5, 5, 5, 5, 5],
          silent: true,
        },
        ]
        */
    }
    setChartOptions(chartOptions)
  }, [data, isMobile, allYears])

  return (
    <Columnchart className={isMobile ? 'mobile' : 'desktop'}>
      <div className='graphic'>
        <ReactECharts
          ref={chartRef}
          option={chartOptions}
          theme={colorMode === 'dark' ? 'ta-dark' : 'ta'}
          height={
            isDetailView
              ? isMobile
                ? '290px'
                : '310px'
              : isMobile
              ? '150px'
              : '160px'
          }
          onClicked={(e) => {
            handleChartClick()
          }}
        ></ReactECharts>
      </div>
    </Columnchart>
  )
}
const Columnchart = styled.div`
  color: var(--text-color);
`

export default ColumnChart
